import React, { useState, useEffect } from 'react';
import { Link, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { useAuth, API } from '../App';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Globe, LayoutDashboard, BookOpen, Users, CreditCard,
  LogOut, Plus, Edit, Trash2, Menu, X, ChevronRight,
  DollarSign, TrendingUp, GripVertical, Play, FileText, Upload, Download,
  MessageSquare, Calendar, Send, Loader2, Bell, Settings, Ban,
  Eye, EyeOff, Clock, Search, Filter, Package, UserCog, Megaphone,
  CheckCircle, XCircle, AlertCircle, RefreshCw, ChevronDown, ChevronUp, MessageCircle, Reply
} from 'lucide-react';

// ==================== SIDEBAR ====================
function AdminSidebar({ isOpen, onClose, notificationCount }) {
  var location = useLocation();
  var { logout } = useAuth();
  var navigate = useNavigate();

  var navItems = [
    { path: '/admin', label: 'Dashboard', icon: LayoutDashboard, exact: true },
    { path: '/admin/users', label: 'User Management', icon: Users },
    { path: '/admin/packages', label: 'Package Pricing', icon: Package },
    { path: '/admin/courses', label: 'Course Content', icon: BookOpen },
    { path: '/admin/comments', label: 'Video Comments', icon: MessageCircle },
    { path: '/admin/purchases', label: 'Transactions', icon: CreditCard },
    { path: '/admin/pending-payments', label: 'Pending Payments', icon: Clock },
    { path: '/admin/tickets', label: 'Support Tickets', icon: MessageSquare },
    { path: '/admin/bookings', label: 'Zoom Bookings', icon: Calendar },
    { path: '/admin/announcements', label: 'Announcements', icon: Megaphone },
    { path: '/admin/templates', label: 'Templates', icon: FileText },
  ];

  function handleLogout() {
    logout();
    navigate('/');
  }

  var navElements = navItems.map(function(item) {
    var itemPath = item.path;
    var isActive = item.exact 
      ? location.pathname === itemPath 
      : location.pathname.startsWith(itemPath);
    
    return React.createElement(Link, {
      key: itemPath,
      to: itemPath,
      onClick: function() { if (window.innerWidth < 1024) onClose(); },
      className: 'flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ' + 
        (isActive ? 'bg-red-600 text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-white'),
      'data-testid': 'admin-nav-' + item.label.toLowerCase().replace(/ /g, '-')
    },
      React.createElement(item.icon, { className: 'w-5 h-5' }),
      React.createElement('span', null, item.label)
    );
  });

  return React.createElement('aside', {
    className: 'fixed lg:static inset-y-0 left-0 z-40 w-64 bg-slate-900 transform transition-transform duration-300 ' +
      (isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0')
  },
    React.createElement('div', { className: 'flex flex-col h-full' },
      // Logo
      React.createElement('div', { className: 'p-6 border-b border-slate-800' },
        React.createElement(Link, { to: '/', className: 'flex items-center gap-2' },
          React.createElement('div', { className: 'w-10 h-10 rounded-full bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center' },
            React.createElement(Globe, { className: 'w-5 h-5 text-white' })
          ),
          React.createElement('div', null,
            React.createElement('span', { className: 'font-bold text-white block' }, 'Admin Panel'),
            React.createElement('p', { className: 'text-xs text-slate-500' }, 'innovatorfoundervisas.com')
          )
        )
      ),
      // Navigation
      React.createElement('nav', { className: 'flex-1 p-4 space-y-1 overflow-y-auto' }, navElements),
      // Logout
      React.createElement('div', { className: 'p-4 border-t border-slate-800' },
        React.createElement('button', {
          onClick: handleLogout,
          className: 'flex items-center gap-3 px-4 py-3 text-slate-400 hover:text-red-400 transition-colors w-full',
          'data-testid': 'admin-logout-btn'
        },
          React.createElement(LogOut, { className: 'w-5 h-5' }),
          React.createElement('span', null, 'Logout')
        )
      )
    )
  );
}

// ==================== STAT CARD ====================
function StatCard({ title, value, icon: Icon, color, trend }) {
  var colorClasses = {
    red: 'bg-red-50 text-red-600 border-red-100',
    blue: 'bg-blue-50 text-blue-600 border-blue-100',
    green: 'bg-green-50 text-green-600 border-green-100',
    amber: 'bg-amber-50 text-amber-600 border-amber-100',
    purple: 'bg-purple-50 text-purple-600 border-purple-100'
  };
  
  return React.createElement('div', {
    className: 'bg-white rounded-xl p-6 border border-slate-200 hover:shadow-lg transition-shadow'
  },
    React.createElement('div', { className: 'flex items-center justify-between mb-4' },
      React.createElement('div', { className: 'w-12 h-12 rounded-xl flex items-center justify-center ' + (colorClasses[color] || colorClasses.blue) },
        React.createElement(Icon, { className: 'w-6 h-6' })
      ),
      trend ? React.createElement('span', {
        className: 'text-sm font-medium ' + (trend > 0 ? 'text-green-600' : 'text-red-600')
      }, (trend > 0 ? '+' : '') + trend + '%') : null
    ),
    React.createElement('p', { className: 'text-sm text-slate-500 mb-1' }, title),
    React.createElement('p', { className: 'text-2xl font-bold text-slate-900' }, value)
  );
}

// ==================== DASHBOARD OVERVIEW ====================
function DashboardOverview() {
  var { token } = useAuth();
  var [stats, setStats] = useState(null);
  var [loading, setLoading] = useState(true);

  useEffect(function() {
    fetchStats();
  }, []);

  function fetchStats() {
    setLoading(true);
    axios.get(API + '/admin/enhanced-stats', {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function(res) {
      setStats(res.data);
      setLoading(false);
    }).catch(function() {
      // Fallback to basic stats
      axios.get(API + '/admin/stats', {
        headers: { Authorization: 'Bearer ' + token }
      }).then(function(res) {
        setStats({
          users: { total: res.data.total_users || 0 },
          purchases: { total: res.data.total_purchases || 0 },
          revenue: { total: res.data.package_stats?.reduce(function(sum, p) { return sum + (p.revenue || 0); }, 0) || 0, by_package: res.data.package_stats || [] },
          support: { open_tickets: 0 },
          bookings: { pending: 0 }
        });
        setLoading(false);
      }).catch(function() {
        setLoading(false);
      });
    });
  }

  if (loading) return React.createElement(LoadingSpinner);

  return React.createElement('div', { 'data-testid': 'admin-overview' },
    React.createElement('div', { className: 'flex items-center justify-between mb-6' },
      React.createElement('h1', { className: 'text-2xl font-bold text-slate-900' }, 'Dashboard Overview'),
      React.createElement(Button, {
        onClick: fetchStats,
        variant: 'outline',
        size: 'sm'
      },
        React.createElement(RefreshCw, { className: 'w-4 h-4 mr-2' }),
        'Refresh'
      )
    ),
    
    // Stats Grid
    React.createElement('div', { className: 'grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8' },
      React.createElement(StatCard, {
        title: 'Total Users',
        value: stats?.users?.total || 0,
        icon: Users,
        color: 'blue',
        trend: stats?.users?.new_this_week
      }),
      React.createElement(StatCard, {
        title: 'Total Revenue',
        value: '£' + ((stats?.revenue?.total || 0).toFixed(2)),
        icon: DollarSign,
        color: 'green'
      }),
      React.createElement(StatCard, {
        title: 'Open Tickets',
        value: stats?.support?.open_tickets || 0,
        icon: MessageSquare,
        color: 'amber'
      }),
      React.createElement(StatCard, {
        title: 'Pending Payments',
        value: stats?.purchases?.pending || 0,
        icon: Clock,
        color: 'purple'
      })
    ),

    // Quick Actions
    React.createElement('div', { className: 'grid md:grid-cols-3 gap-6 mb-8' },
      React.createElement(Link, { to: '/admin/tickets', className: 'bg-amber-50 border border-amber-200 rounded-xl p-6 hover:shadow-md transition-shadow' },
        React.createElement('div', { className: 'flex items-center gap-4' },
          React.createElement(MessageSquare, { className: 'w-8 h-8 text-amber-600' }),
          React.createElement('div', null,
            React.createElement('p', { className: 'font-semibold text-slate-900' }, 'Support Tickets'),
            React.createElement('p', { className: 'text-sm text-slate-600' }, (stats?.support?.open_tickets || 0) + ' open, ' + (stats?.support?.in_progress || 0) + ' in progress')
          )
        )
      ),
      React.createElement(Link, { to: '/admin/pending-payments', className: 'bg-purple-50 border border-purple-200 rounded-xl p-6 hover:shadow-md transition-shadow' },
        React.createElement('div', { className: 'flex items-center gap-4' },
          React.createElement(Clock, { className: 'w-8 h-8 text-purple-600' }),
          React.createElement('div', null,
            React.createElement('p', { className: 'font-semibold text-slate-900' }, 'Pending Payments'),
            React.createElement('p', { className: 'text-sm text-slate-600' }, (stats?.purchases?.pending || 0) + ' awaiting verification')
          )
        )
      ),
      React.createElement(Link, { to: '/admin/bookings', className: 'bg-blue-50 border border-blue-200 rounded-xl p-6 hover:shadow-md transition-shadow' },
        React.createElement('div', { className: 'flex items-center gap-4' },
          React.createElement(Calendar, { className: 'w-8 h-8 text-blue-600' }),
          React.createElement('div', null,
            React.createElement('p', { className: 'font-semibold text-slate-900' }, 'Zoom Bookings'),
            React.createElement('p', { className: 'text-sm text-slate-600' }, (stats?.bookings?.pending || 0) + ' pending, ' + (stats?.bookings?.confirmed || 0) + ' confirmed')
          )
        )
      )
    ),

    // Revenue by Package
    React.createElement('div', { className: 'bg-white rounded-xl p-6 border border-slate-200' },
      React.createElement('h2', { className: 'text-lg font-bold text-slate-900 mb-4' }, 'Revenue by Package'),
      React.createElement('div', { className: 'space-y-4' },
        stats?.revenue?.by_package?.map(function(pkg) {
          return React.createElement('div', {
            key: pkg._id,
            className: 'flex items-center justify-between p-4 bg-slate-50 rounded-lg'
          },
            React.createElement('div', null,
              React.createElement('p', { className: 'font-medium text-slate-900 capitalize' }, (pkg._id || 'Unknown') + ' Package'),
              React.createElement('p', { className: 'text-sm text-slate-500' }, pkg.count + ' sales')
            ),
            React.createElement('p', { className: 'text-xl font-bold text-green-600 font-mono' }, '£' + (pkg.revenue || 0).toFixed(2))
          );
        }) || React.createElement('p', { className: 'text-slate-500 text-center py-4' }, 'No sales yet')
      )
    )
  );
}

// ==================== USER MANAGEMENT ====================
function UserManagement() {
  var { token } = useAuth();
  var [users, setUsers] = useState([]);
  var [loading, setLoading] = useState(true);
  var [search, setSearch] = useState('');
  var [selectedUser, setSelectedUser] = useState(null);
  var [userDetails, setUserDetails] = useState(null);
  var [detailsLoading, setDetailsLoading] = useState(false);
  var [actionModal, setActionModal] = useState(null);

  useEffect(function() {
    fetchUsers();
  }, []);

  function fetchUsers() {
    setLoading(true);
    axios.get(API + '/admin/users/detailed', {
      headers: { Authorization: 'Bearer ' + token },
      params: { search: search || undefined }
    }).then(function(res) {
      setUsers(res.data.users || []);
      setLoading(false);
    }).catch(function() {
      // Fallback
      axios.get(API + '/admin/users', {
        headers: { Authorization: 'Bearer ' + token }
      }).then(function(res) {
        setUsers(res.data || []);
        setLoading(false);
      }).catch(function() {
        setLoading(false);
      });
    });
  }

  function viewUserDetails(userId) {
    setDetailsLoading(true);
    setSelectedUser(userId);
    axios.get(API + '/admin/users/' + userId + '/details', {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function(res) {
      setUserDetails(res.data);
      setDetailsLoading(false);
    }).catch(function() {
      toast.error('Failed to load user details');
      setDetailsLoading(false);
    });
  }

  function blockUser(userId, block) {
    axios.post(API + '/admin/users/' + userId + '/block', {
      is_blocked: block,
      reason: block ? 'Blocked by admin' : null
    }, {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function() {
      toast.success(block ? 'User blocked' : 'User unblocked');
      fetchUsers();
      if (userDetails) viewUserDetails(userId);
    }).catch(function() {
      toast.error('Failed to update user');
    });
  }

  function assignPackage(userId, packageType) {
    axios.post(API + '/admin/users/' + userId + '/assign-package', {
      package_type: packageType
    }, {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function() {
      toast.success('Package assigned successfully');
      setActionModal(null);
      fetchUsers();
      if (userDetails) viewUserDetails(userId);
    }).catch(function(err) {
      toast.error(err.response?.data?.detail || 'Failed to assign package');
    });
  }

  function adminResetPassword(userId, newPassword) {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    axios.post(API + '/admin/users/' + userId + '/reset-password', {
      new_password: newPassword
    }, {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function() {
      toast.success('Password reset successfully');
      setActionModal(null);
    }).catch(function(err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    });
  }

  function adjustCredits(userId, creditType, amount, reason) {
    axios.post(API + '/admin/users/' + userId + '/adjust-credits', {
      credit_type: creditType,
      amount: parseInt(amount),
      reason: reason
    }, {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function() {
      toast.success('Credits adjusted');
      setActionModal(null);
      if (userDetails) viewUserDetails(userId);
    }).catch(function(err) {
      toast.error(err.response?.data?.detail || 'Failed to adjust credits');
    });
  }

  if (loading) return React.createElement(LoadingSpinner);

  // Build user rows using map for proper closure
  var userRows = users.map(function(user) {
    var userId = user.id;
    var userName = user.name;
    var userEmail = user.email;
    var userPackages = user.packages || [];
    var userCredits = user.ai_credits || 0;
    var isBlocked = user.is_blocked;
    
    return React.createElement('tr', { key: userId, className: 'hover:bg-slate-50' },
      React.createElement('td', { className: 'px-4 py-4' },
        React.createElement('div', { className: 'flex items-center gap-3' },
          React.createElement('div', { className: 'w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center' },
            React.createElement('span', { className: 'text-slate-600 font-medium' }, (userName || 'U')[0].toUpperCase())
          ),
          React.createElement('div', null,
            React.createElement('p', { className: 'font-medium text-slate-900' }, userName || 'Unknown'),
            React.createElement('p', { className: 'text-sm text-slate-500' }, userEmail)
          )
        )
      ),
      React.createElement('td', { className: 'px-4 py-4' },
        React.createElement('div', { className: 'flex flex-wrap gap-1' },
          userPackages.length > 0 
            ? userPackages.map(function(pkg) {
                return React.createElement(Badge, { key: pkg, className: 'bg-green-100 text-green-700' }, pkg);
              })
            : React.createElement(Badge, { className: 'bg-slate-100 text-slate-600' }, 'Free')
        )
      ),
      React.createElement('td', { className: 'px-4 py-4 text-center' },
        React.createElement('span', { className: 'font-mono text-slate-700' }, userCredits)
      ),
      React.createElement('td', { className: 'px-4 py-4' },
        isBlocked 
          ? React.createElement(Badge, { className: 'bg-red-100 text-red-700' }, 'Blocked')
          : React.createElement(Badge, { className: 'bg-green-100 text-green-700' }, 'Active')
      ),
      React.createElement('td', { className: 'px-4 py-4' },
        React.createElement('div', { className: 'flex gap-2' },
          React.createElement(Button, {
            size: 'sm',
            variant: 'outline',
            onClick: function() { viewUserDetails(userId); },
            'data-testid': 'view-user-' + userId
          },
            React.createElement(Eye, { className: 'w-4 h-4' })
          ),
          React.createElement(Button, {
            size: 'sm',
            variant: 'outline',
            onClick: function() { setActionModal({ type: 'assign', user: { id: userId, name: userName, email: userEmail } }); },
            'data-testid': 'assign-pkg-' + userId
          },
            React.createElement(Package, { className: 'w-4 h-4' })
          ),
          React.createElement(Button, {
            size: 'sm',
            variant: isBlocked ? 'default' : 'destructive',
            onClick: function() { blockUser(userId, !isBlocked); },
            'data-testid': 'block-user-' + userId
          },
            React.createElement(Ban, { className: 'w-4 h-4' })
          )
        )
      )
    );
  });

  return React.createElement('div', null,
    React.createElement('div', { className: 'flex items-center justify-between mb-6' },
      React.createElement('h1', { className: 'text-2xl font-bold text-slate-900' }, 'User Management'),
      React.createElement('div', { className: 'flex gap-2' },
        React.createElement('div', { className: 'relative' },
          React.createElement(Search, { className: 'w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400' }),
          React.createElement(Input, {
            value: search,
            onChange: function(e) { setSearch(e.target.value); },
            placeholder: 'Search users...',
            className: 'pl-10 w-64',
            onKeyPress: function(e) { if (e.key === 'Enter') fetchUsers(); }
          })
        ),
        React.createElement(Button, { onClick: fetchUsers }, 'Search')
      )
    ),

    // Users Table
    React.createElement('div', { className: 'bg-white rounded-xl border border-slate-200 overflow-hidden' },
      React.createElement('table', { className: 'w-full' },
        React.createElement('thead', { className: 'bg-slate-50' },
          React.createElement('tr', null,
            React.createElement('th', { className: 'px-4 py-3 text-left text-sm font-medium text-slate-700' }, 'User'),
            React.createElement('th', { className: 'px-4 py-3 text-left text-sm font-medium text-slate-700' }, 'Packages'),
            React.createElement('th', { className: 'px-4 py-3 text-center text-sm font-medium text-slate-700' }, 'AI Credits'),
            React.createElement('th', { className: 'px-4 py-3 text-left text-sm font-medium text-slate-700' }, 'Status'),
            React.createElement('th', { className: 'px-4 py-3 text-left text-sm font-medium text-slate-700' }, 'Actions')
          )
        ),
        React.createElement('tbody', { className: 'divide-y divide-slate-100' },
          users.length === 0 
            ? React.createElement('tr', null, React.createElement('td', { colSpan: 5, className: 'px-4 py-8 text-center text-slate-500' }, 'No users found'))
            : userRows
        )
      )
    ),

    // User Details Modal
    React.createElement(Dialog, { open: !!selectedUser, onOpenChange: function() { setSelectedUser(null); setUserDetails(null); } },
      React.createElement(DialogContent, { className: 'max-w-4xl max-h-[90vh] overflow-y-auto' },
        React.createElement(DialogTitle, null, 'User Details'),
        detailsLoading 
          ? React.createElement(LoadingSpinner)
          : userDetails && React.createElement('div', { className: 'space-y-6' },
              // User Info
              React.createElement('div', { className: 'grid md:grid-cols-2 gap-6' },
                React.createElement('div', { className: 'bg-slate-50 rounded-xl p-4' },
                  React.createElement('h3', { className: 'font-semibold text-slate-900 mb-3' }, 'Account Info'),
                  React.createElement('div', { className: 'space-y-2 text-sm' },
                    React.createElement('p', null, React.createElement('span', { className: 'text-slate-500' }, 'Name: '), userDetails.user?.name),
                    React.createElement('p', null, React.createElement('span', { className: 'text-slate-500' }, 'Email: '), userDetails.user?.email),
                    React.createElement('p', null, React.createElement('span', { className: 'text-slate-500' }, 'Phone: '), userDetails.user?.phone || 'N/A'),
                    React.createElement('p', null, React.createElement('span', { className: 'text-slate-500' }, 'Country: '), userDetails.user?.country || 'N/A'),
                    React.createElement('p', null, React.createElement('span', { className: 'text-slate-500' }, 'Joined: '), new Date(userDetails.user?.created_at).toLocaleDateString())
                  )
                ),
                React.createElement('div', { className: 'bg-slate-50 rounded-xl p-4' },
                  React.createElement('h3', { className: 'font-semibold text-slate-900 mb-3' }, 'Credits & Packages'),
                  React.createElement('div', { className: 'space-y-2 text-sm' },
                    React.createElement('p', null, React.createElement('span', { className: 'text-slate-500' }, 'AI Credits: '), React.createElement('span', { className: 'font-mono font-bold text-green-600' }, userDetails.credits?.ai_credits || 0)),
                    React.createElement('p', null, React.createElement('span', { className: 'text-slate-500' }, 'Interview Credits: '), React.createElement('span', { className: 'font-mono font-bold text-blue-600' }, userDetails.credits?.interview_credits || 0)),
                    React.createElement('p', null, React.createElement('span', { className: 'text-slate-500' }, 'Packages: '), (userDetails.user?.packages || []).join(', ') || 'None')
                  ),
                  React.createElement('div', { className: 'flex gap-2 mt-4' },
                    React.createElement(Button, {
                      size: 'sm',
                      onClick: function() { setActionModal({ type: 'credits', user: userDetails.user }); }
                    }, 'Adjust Credits'),
                    React.createElement(Button, {
                      size: 'sm',
                      variant: 'outline',
                      onClick: function() { setActionModal({ type: 'assign', user: userDetails.user }); }
                    }, 'Assign Package'),
                    React.createElement(Button, {
                      size: 'sm',
                      variant: 'outline',
                      className: 'text-amber-600 border-amber-300 hover:bg-amber-50',
                      onClick: function() { setActionModal({ type: 'password', user: userDetails.user }); }
                    }, 'Reset Password')
                  )
                )
              ),
              
              // Locked Idea
              userDetails.ideas?.locked_idea && React.createElement('div', { className: 'bg-amber-50 rounded-xl p-4 border border-amber-200' },
                React.createElement('h3', { className: 'font-semibold text-amber-900 mb-2' }, 'Locked Business Idea'),
                React.createElement('p', { className: 'font-medium text-amber-800' }, userDetails.ideas.locked_idea.business_name),
                React.createElement('p', { className: 'text-sm text-amber-700' }, userDetails.ideas.locked_idea.one_liner)
              ),
              
              // Billing History
              React.createElement('div', null,
                React.createElement('h3', { className: 'font-semibold text-slate-900 mb-3' }, 'Billing History'),
                React.createElement('div', { className: 'bg-white rounded-lg border border-slate-200 overflow-hidden' },
                  React.createElement('table', { className: 'w-full text-sm' },
                    React.createElement('thead', { className: 'bg-slate-50' },
                      React.createElement('tr', null,
                        React.createElement('th', { className: 'px-4 py-2 text-left' }, 'Date'),
                        React.createElement('th', { className: 'px-4 py-2 text-left' }, 'Package'),
                        React.createElement('th', { className: 'px-4 py-2 text-left' }, 'Amount'),
                        React.createElement('th', { className: 'px-4 py-2 text-left' }, 'Status')
                      )
                    ),
                    React.createElement('tbody', { className: 'divide-y' },
                      (userDetails.billing?.purchases || []).slice(0, 10).map(function(p) {
                        return React.createElement('tr', { key: p.id },
                          React.createElement('td', { className: 'px-4 py-2' }, new Date(p.created_at).toLocaleDateString()),
                          React.createElement('td', { className: 'px-4 py-2 capitalize' }, p.package_type),
                          React.createElement('td', { className: 'px-4 py-2' }, '£' + (p.amount || 0).toFixed(2)),
                          React.createElement('td', { className: 'px-4 py-2' },
                            React.createElement(Badge, { className: p.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700' }, p.status)
                          )
                        );
                      })
                    )
                  ),
                  (userDetails.billing?.purchases || []).length === 0 && React.createElement('p', { className: 'text-center text-slate-500 py-4' }, 'No purchases')
                )
              ),
              
              // Total Spent
              React.createElement('div', { className: 'bg-green-50 rounded-xl p-4 border border-green-200' },
                React.createElement('p', { className: 'text-sm text-green-700' }, 'Total Spent'),
                React.createElement('p', { className: 'text-2xl font-bold text-green-800' }, '£' + (userDetails.billing?.total_spent || 0).toFixed(2))
              )
            )
      )
    ),

    // Action Modal (Assign Package / Adjust Credits / Reset Password)
    React.createElement(Dialog, { open: !!actionModal, onOpenChange: function() { setActionModal(null); } },
      React.createElement(DialogContent, null,
        actionModal?.type === 'assign' && React.createElement('div', null,
          React.createElement(DialogTitle, null, 'Assign Package to ' + (actionModal.user?.name || 'User')),
          React.createElement(DialogDescription, null, 'Select a package to force-activate for this user'),
          React.createElement('div', { className: 'grid gap-3 mt-4' },
            ['video', 'complete', 'premium'].map(function(pkg) {
              var pkgLabels = { video: 'Video Course', complete: 'Complete Package', premium: 'Premium Service' };
              return React.createElement(Button, {
                key: pkg,
                variant: 'outline',
                className: 'justify-start',
                onClick: function() { assignPackage(actionModal.user.id, pkg); }
              },
                React.createElement(Package, { className: 'w-4 h-4 mr-2' }),
                pkgLabels[pkg]
              );
            })
          )
        ),
        actionModal?.type === 'password' && React.createElement(PasswordResetForm, {
          user: actionModal.user,
          onSubmit: function(newPassword) { adminResetPassword(actionModal.user.id, newPassword); },
          onCancel: function() { setActionModal(null); }
        }),
        actionModal?.type === 'credits' && React.createElement(CreditAdjustForm, {
          user: actionModal.user,
          onSubmit: function(type, amount, reason) { adjustCredits(actionModal.user.id, type, amount, reason); },
          onCancel: function() { setActionModal(null); }
        })
      )
    )
  );
}

// Credit Adjustment Form Component
function CreditAdjustForm({ user, onSubmit, onCancel }) {
  var [creditType, setCreditType] = useState('ai_credits');
  var [amount, setAmount] = useState('');
  var [reason, setReason] = useState('');

  return React.createElement('div', null,
    React.createElement(DialogTitle, null, 'Adjust Credits for ' + (user?.name || 'User')),
    React.createElement('div', { className: 'space-y-4 mt-4' },
      React.createElement('div', null,
        React.createElement('label', { className: 'block text-sm font-medium text-slate-700 mb-1' }, 'Credit Type'),
        React.createElement('select', {
          value: creditType,
          onChange: function(e) { setCreditType(e.target.value); },
          className: 'w-full px-3 py-2 border border-slate-300 rounded-lg'
        },
          React.createElement('option', { value: 'ai_credits' }, 'AI Credits'),
          React.createElement('option', { value: 'interview_credits' }, 'Interview Credits')
        )
      ),
      React.createElement('div', null,
        React.createElement('label', { className: 'block text-sm font-medium text-slate-700 mb-1' }, 'Amount (positive to add, negative to deduct)'),
        React.createElement(Input, {
          type: 'number',
          value: amount,
          onChange: function(e) { setAmount(e.target.value); },
          placeholder: 'e.g. 50 or -10'
        })
      ),
      React.createElement('div', null,
        React.createElement('label', { className: 'block text-sm font-medium text-slate-700 mb-1' }, 'Reason'),
        React.createElement(Input, {
          value: reason,
          onChange: function(e) { setReason(e.target.value); },
          placeholder: 'Why are you adjusting credits?'
        })
      ),
      React.createElement('div', { className: 'flex gap-2' },
        React.createElement(Button, { variant: 'outline', onClick: onCancel }, 'Cancel'),
        React.createElement(Button, {
          onClick: function() { onSubmit(creditType, amount, reason); },
          disabled: !amount || !reason
        }, 'Adjust Credits')
      )
    )
  );
}

// Password Reset Form Component
function PasswordResetForm({ user, onSubmit, onCancel }) {
  var [newPassword, setNewPassword] = useState('');
  var [confirmPassword, setConfirmPassword] = useState('');
  var [showPassword, setShowPassword] = useState(false);

  return React.createElement('div', { className: 'space-y-4' },
    React.createElement(DialogTitle, null, 'Reset Password'),
    React.createElement(DialogDescription, null, 
      'Set a new password for ', 
      React.createElement('strong', null, user?.name || user?.email)
    ),
    React.createElement('div', null,
      React.createElement('label', { className: 'block text-sm font-medium text-slate-700 mb-1' }, 'New Password'),
      React.createElement('div', { className: 'relative' },
        React.createElement(Input, {
          type: showPassword ? 'text' : 'password',
          value: newPassword,
          onChange: function(e) { setNewPassword(e.target.value); },
          placeholder: 'Enter new password (min 6 chars)'
        }),
        React.createElement('button', {
          type: 'button',
          className: 'absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600',
          onClick: function() { setShowPassword(!showPassword); }
        }, showPassword ? React.createElement(EyeOff, { className: 'w-4 h-4' }) : React.createElement(Eye, { className: 'w-4 h-4' }))
      )
    ),
    React.createElement('div', null,
      React.createElement('label', { className: 'block text-sm font-medium text-slate-700 mb-1' }, 'Confirm Password'),
      React.createElement(Input, {
        type: showPassword ? 'text' : 'password',
        value: confirmPassword,
        onChange: function(e) { setConfirmPassword(e.target.value); },
        placeholder: 'Confirm new password'
      })
    ),
    newPassword && confirmPassword && newPassword !== confirmPassword && 
      React.createElement('p', { className: 'text-red-500 text-sm' }, 'Passwords do not match'),
    React.createElement('div', { className: 'flex gap-2' },
      React.createElement(Button, { variant: 'outline', onClick: onCancel }, 'Cancel'),
      React.createElement(Button, {
        onClick: function() { onSubmit(newPassword); },
        disabled: !newPassword || newPassword.length < 6 || newPassword !== confirmPassword
      }, 'Reset Password')
    )
  );
}

// ==================== PACKAGE PRICING ====================
function PackagePricing() {
  var { token } = useAuth();
  var [packages, setPackages] = useState([]);
  var [addons, setAddons] = useState([]);
  var [loading, setLoading] = useState(true);
  var [editingItem, setEditingItem] = useState(null);
  var [newPrice, setNewPrice] = useState('');
  var [saving, setSaving] = useState(false);

  useEffect(function() {
    fetchPackages();
  }, []);

  function fetchPackages() {
    setLoading(true);
    axios.get(API + '/admin/packages', {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function(res) {
      setPackages(res.data.packages || []);
      setAddons(res.data.addons || []);
      setLoading(false);
    }).catch(function() {
      // Fallback packages
      setPackages([
        { name: 'video', display_name: 'Video Package', price: 29.99, currency: 'GBP', description: 'Comprehensive video course', type: 'main' },
        { name: 'complete', display_name: 'Complete Package', price: 149.00, currency: 'GBP', description: 'Video Course + AI Proposal Builder', type: 'main' },
        { name: 'premium', display_name: 'Premium Package', price: 1999.00, currency: 'GBP', description: 'Full done-for-you service', type: 'main' }
      ]);
      setAddons([
        { name: 'ai_credits_20', display_name: 'AI Credits (20)', price: 9.99, currency: 'GBP', credits: 20, addon_type: 'ai', type: 'addon' },
        { name: 'ai_credits_50', display_name: 'AI Credits (50)', price: 19.99, currency: 'GBP', credits: 50, addon_type: 'ai', type: 'addon' },
        { name: 'interview_1', display_name: 'Mock Interview (1)', price: 9.99, currency: 'GBP', credits: 1, addon_type: 'interview', type: 'addon' },
        { name: 'zoom_call', display_name: 'Zoom Consultation', price: 49.00, currency: 'GBP', addon_type: 'zoom', type: 'addon' }
      ]);
      setLoading(false);
    });
  }

  function updatePrice(itemName, itemType) {
    setSaving(true);
    var endpoint = itemType === 'addon' 
      ? API + '/admin/addons/' + itemName + '/price'
      : API + '/admin/packages/' + itemName + '/price';
    
    axios.put(endpoint, {
      price: parseFloat(newPrice),
      currency: 'GBP'
    }, {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function() {
      toast.success('Price updated successfully');
      setEditingItem(null);
      setNewPrice('');
      setSaving(false);
      fetchPackages();
    }).catch(function() {
      toast.error('Failed to update price');
      setSaving(false);
    });
  }

  function startEditing(itemName, currentPrice) {
    setEditingItem(itemName);
    setNewPrice(String(currentPrice));
  }

  function cancelEditing() {
    setEditingItem(null);
    setNewPrice('');
  }

  function handlePriceChange(e) {
    setNewPrice(e.target.value);
  }

  function handleSavePackage(pkgName) {
    updatePrice(pkgName, 'main');
  }

  function handleSaveAddon(addonName) {
    updatePrice(addonName, 'addon');
  }

  function getAddonIcon(addonType) {
    if (addonType === 'ai') return React.createElement('span', { className: 'text-2xl' }, '🤖');
    if (addonType === 'interview') return React.createElement('span', { className: 'text-2xl' }, '🎤');
    if (addonType === 'zoom') return React.createElement('span', { className: 'text-2xl' }, '📹');
    return React.createElement('span', { className: 'text-2xl' }, '📦');
  }

  function getPackageColor(name) {
    if (name === 'video') return 'border-blue-200 bg-blue-50';
    if (name === 'complete') return 'border-green-200 bg-green-50';
    if (name === 'premium') return 'border-amber-200 bg-amber-50';
    return 'border-slate-200 bg-white';
  }

  if (loading) return React.createElement(LoadingSpinner);

  // Build package cards using map with proper closure
  var packageCards = packages.map(function(pkg) {
    var isEditing = editingItem === pkg.name;
    var colorClass = getPackageColor(pkg.name);
    var pkgName = pkg.name;
    var pkgPrice = pkg.price;
    
    return React.createElement('div', {
      key: pkgName,
      className: 'rounded-xl border-2 p-6 transition-shadow hover:shadow-lg ' + colorClass
    },
      React.createElement('div', { className: 'flex items-center justify-between mb-4' },
        React.createElement('div', null,
          React.createElement('h3', { className: 'text-lg font-bold text-slate-900 capitalize' }, pkg.display_name || (pkgName + ' Package')),
          React.createElement(Badge, { className: 'bg-slate-100 text-slate-600 mt-1' }, 'Main Package')
        ),
        React.createElement(Button, {
          size: 'sm',
          variant: 'ghost',
          onClick: function() { startEditing(pkgName, pkgPrice); },
          'data-testid': 'edit-pkg-' + pkgName
        },
          React.createElement(Edit, { className: 'w-4 h-4' })
        )
      ),
      isEditing
        ? React.createElement('div', { className: 'space-y-3' },
            React.createElement('div', { className: 'flex items-center gap-2' },
              React.createElement('span', { className: 'text-slate-500 font-medium' }, '£'),
              React.createElement(Input, {
                type: 'number',
                step: '0.01',
                value: newPrice,
                onChange: handlePriceChange,
                className: 'flex-1',
                placeholder: 'Enter new price',
                'data-testid': 'price-input-' + pkgName
              })
            ),
            React.createElement('div', { className: 'flex gap-2' },
              React.createElement(Button, { 
                size: 'sm', 
                onClick: function() { handleSavePackage(pkgName); },
                disabled: saving || !newPrice,
                'data-testid': 'save-pkg-' + pkgName
              }, saving ? 'Saving...' : 'Save'),
              React.createElement(Button, { 
                size: 'sm', 
                variant: 'outline', 
                onClick: cancelEditing,
                'data-testid': 'cancel-pkg-' + pkgName
              }, 'Cancel')
            )
          )
        : React.createElement('div', null,
            React.createElement('p', { className: 'text-3xl font-bold text-green-600' }, '£' + (pkgPrice || 0).toFixed(2)),
            React.createElement('p', { className: 'text-sm text-slate-500 mt-2' }, pkg.description || 'Package description')
          )
    );
  });

  // Build addon cards using map with proper closure
  var addonCards = addons.map(function(addon) {
    var isEditingAddon = editingItem === addon.name;
    var addonName = addon.name;
    var addonPrice = addon.price;
    
    return React.createElement('div', {
      key: addonName,
      className: 'bg-white rounded-xl border border-slate-200 p-5 transition-shadow hover:shadow-md'
    },
      React.createElement('div', { className: 'flex items-start justify-between mb-3' },
        React.createElement('div', { className: 'flex items-center gap-3' },
          getAddonIcon(addon.addon_type),
          React.createElement('div', null,
            React.createElement('h4', { className: 'font-semibold text-slate-900' }, addon.display_name || addonName),
            React.createElement('div', { className: 'flex gap-2 mt-1' },
              React.createElement(Badge, { 
                className: addon.addon_type === 'ai' ? 'bg-purple-100 text-purple-700' :
                          addon.addon_type === 'interview' ? 'bg-blue-100 text-blue-700' :
                          'bg-green-100 text-green-700'
              }, addon.addon_type === 'ai' ? 'AI Credits' : addon.addon_type === 'interview' ? 'Interview' : 'Consultation'),
              addon.credits && React.createElement(Badge, { className: 'bg-slate-100 text-slate-600' }, addon.credits + ' credits')
            )
          )
        ),
        React.createElement(Button, {
          size: 'sm',
          variant: 'ghost',
          onClick: function() { startEditing(addonName, addonPrice); },
          'data-testid': 'edit-addon-' + addonName
        },
          React.createElement(Edit, { className: 'w-4 h-4' })
        )
      ),
      isEditingAddon
        ? React.createElement('div', { className: 'space-y-3' },
            React.createElement('div', { className: 'flex items-center gap-2' },
              React.createElement('span', { className: 'text-slate-500 font-medium' }, '£'),
              React.createElement(Input, {
                type: 'number',
                step: '0.01',
                value: newPrice,
                onChange: handlePriceChange,
                className: 'flex-1',
                placeholder: 'Enter new price',
                'data-testid': 'price-input-' + addonName
              })
            ),
            React.createElement('div', { className: 'flex gap-2' },
              React.createElement(Button, { 
                size: 'sm', 
                onClick: function() { handleSaveAddon(addonName); },
                disabled: saving || !newPrice,
                'data-testid': 'save-addon-' + addonName
              }, saving ? 'Saving...' : 'Save'),
              React.createElement(Button, { 
                size: 'sm', 
                variant: 'outline', 
                onClick: cancelEditing,
                'data-testid': 'cancel-addon-' + addonName
              }, 'Cancel')
            )
          )
        : React.createElement('div', null,
            React.createElement('p', { className: 'text-2xl font-bold text-green-600' }, '£' + (addonPrice || 0).toFixed(2)),
            addon.description && React.createElement('p', { className: 'text-xs text-slate-500 mt-1' }, addon.description)
          )
    );
  });

  return React.createElement('div', { 'data-testid': 'package-pricing-page' },
    // Header
    React.createElement('div', { className: 'flex items-center justify-between mb-6' },
      React.createElement('div', null,
        React.createElement('h1', { className: 'text-2xl font-bold text-slate-900' }, 'Package & Pricing Management'),
        React.createElement('p', { className: 'text-slate-500 mt-1' }, 'Manage main packages and add-on feature prices')
      ),
      React.createElement(Button, { 
        variant: 'outline',
        onClick: fetchPackages
      },
        React.createElement(RefreshCw, { className: 'w-4 h-4 mr-2' }),
        'Refresh'
      )
    ),

    // Main Packages Section
    React.createElement('div', { className: 'mb-8' },
      React.createElement('h2', { className: 'text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2' },
        React.createElement(Package, { className: 'w-5 h-5 text-red-500' }),
        'Main Packages'
      ),
      React.createElement('div', { className: 'grid md:grid-cols-3 gap-6' }, packageCards)
    ),

    // Add-on Features Section
    React.createElement('div', null,
      React.createElement('h2', { className: 'text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2' },
        React.createElement(Plus, { className: 'w-5 h-5 text-green-500' }),
        'Add-on Features'
      ),
      React.createElement('div', { className: 'grid sm:grid-cols-2 lg:grid-cols-3 gap-4' }, addonCards)
    )
  );
}

// ==================== PENDING PAYMENTS ====================
function PendingPayments() {
  var { token } = useAuth();
  var [payments, setPayments] = useState([]);
  var [loading, setLoading] = useState(true);

  useEffect(function() {
    fetchPayments();
  }, []);

  function fetchPayments() {
    axios.get(API + '/admin/pending-payments', {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function(res) {
      setPayments(res.data.pending_payments || []);
      setLoading(false);
    }).catch(function() {
      setLoading(false);
    });
  }

  function verifyPayment(purchaseId) {
    axios.post(API + '/admin/pending-payments/' + purchaseId + '/verify', {}, {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function() {
      toast.success('Payment verified and package activated');
      fetchPayments();
    }).catch(function() {
      toast.error('Failed to verify payment');
    });
  }

  function rejectPayment(purchaseId) {
    if (!window.confirm('Are you sure you want to reject this payment?')) return;
    axios.post(API + '/admin/pending-payments/' + purchaseId + '/reject', {}, {
      headers: { Authorization: 'Bearer ' + token },
      params: { reason: 'Payment not received' }
    }).then(function() {
      toast.success('Payment rejected');
      fetchPayments();
    }).catch(function() {
      toast.error('Failed to reject payment');
    });
  }

  if (loading) return React.createElement(LoadingSpinner);

  return React.createElement('div', null,
    React.createElement('h1', { className: 'text-2xl font-bold text-slate-900 mb-6' }, 'Pending Payments'),
    payments.length === 0
      ? React.createElement('div', { className: 'bg-white rounded-xl p-12 border border-slate-200 text-center' },
          React.createElement(CheckCircle, { className: 'w-16 h-16 text-green-500 mx-auto mb-4' }),
          React.createElement('p', { className: 'text-slate-600' }, 'No pending payments to verify')
        )
      : React.createElement('div', { className: 'space-y-4' },
          payments.map(function(payment) {
            var paymentId = payment.id;
            var userName = payment.user?.name || 'Unknown User';
            var userEmail = payment.user?.email;
            var packageType = payment.package_type;
            var amount = payment.amount || 0;
            var paymentMethod = payment.payment_method || '';
            
            return React.createElement('div', {
              key: paymentId,
              className: 'bg-white rounded-xl border border-slate-200 p-6'
            },
              React.createElement('div', { className: 'flex items-center justify-between' },
                React.createElement('div', null,
                  React.createElement('p', { className: 'font-semibold text-slate-900' }, userName),
                  React.createElement('p', { className: 'text-sm text-slate-500' }, userEmail),
                  React.createElement('div', { className: 'flex gap-4 mt-2' },
                    React.createElement('span', { className: 'text-sm' },
                      React.createElement('span', { className: 'text-slate-500' }, 'Package: '),
                      React.createElement('span', { className: 'font-medium capitalize' }, packageType)
                    ),
                    React.createElement('span', { className: 'text-sm' },
                      React.createElement('span', { className: 'text-slate-500' }, 'Amount: '),
                      React.createElement('span', { className: 'font-medium' }, '£' + amount.toFixed(2))
                    ),
                    React.createElement('span', { className: 'text-sm' },
                      React.createElement('span', { className: 'text-slate-500' }, 'Method: '),
                      React.createElement('span', { className: 'font-medium capitalize' }, paymentMethod.replace('_', ' '))
                    )
                  )
                ),
                React.createElement('div', { className: 'flex gap-2' },
                  React.createElement(Button, {
                    onClick: function() { verifyPayment(paymentId); },
                    className: 'bg-green-600 hover:bg-green-700',
                    'data-testid': 'verify-payment-' + paymentId
                  },
                    React.createElement(CheckCircle, { className: 'w-4 h-4 mr-2' }),
                    'Verify & Activate'
                  ),
                  React.createElement(Button, {
                    variant: 'destructive',
                    onClick: function() { rejectPayment(paymentId); },
                    'data-testid': 'reject-payment-' + paymentId
                  },
                    React.createElement(XCircle, { className: 'w-4 h-4 mr-2' }),
                    'Reject'
                  )
                )
              )
            );
          })
        )
  );
}

// ==================== ANNOUNCEMENTS ====================
function AnnouncementsManagement() {
  var { token } = useAuth();
  var [announcements, setAnnouncements] = useState([]);
  var [loading, setLoading] = useState(true);
  var [showCreate, setShowCreate] = useState(false);
  var [title, setTitle] = useState('');
  var [message, setMessage] = useState('');
  var [creating, setCreating] = useState(false);

  useEffect(function() {
    fetchAnnouncements();
  }, []);

  function fetchAnnouncements() {
    axios.get(API + '/admin/announcements', {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function(res) {
      setAnnouncements(res.data.announcements || []);
      setLoading(false);
    }).catch(function() {
      setLoading(false);
    });
  }

  function createAnnouncement() {
    setCreating(true);
    axios.post(API + '/admin/announcements', {
      title: title,
      message: message,
      target: 'all',
      priority: 'normal'
    }, {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function() {
      toast.success('Announcement sent to all users');
      setShowCreate(false);
      setTitle('');
      setMessage('');
      setCreating(false);
      fetchAnnouncements();
    }).catch(function() {
      toast.error('Failed to create announcement');
      setCreating(false);
    });
  }

  function deleteAnnouncement(id) {
    if (!window.confirm('Delete this announcement?')) return;
    axios.delete(API + '/admin/announcements/' + id, {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function() {
      toast.success('Announcement deleted');
      fetchAnnouncements();
    }).catch(function() {
      toast.error('Failed to delete');
    });
  }

  if (loading) return React.createElement(LoadingSpinner);

  return React.createElement('div', null,
    React.createElement('div', { className: 'flex items-center justify-between mb-6' },
      React.createElement('h1', { className: 'text-2xl font-bold text-slate-900' }, 'Announcements'),
      React.createElement(Button, { onClick: function() { setShowCreate(true); } },
        React.createElement(Plus, { className: 'w-4 h-4 mr-2' }),
        'New Announcement'
      )
    ),

    // Create Announcement Modal
    React.createElement(Dialog, { open: showCreate, onOpenChange: setShowCreate },
      React.createElement(DialogContent, null,
        React.createElement(DialogTitle, null, 'Create Announcement'),
        React.createElement(DialogDescription, null, 'This will be sent as a notification to all users'),
        React.createElement('div', { className: 'space-y-4 mt-4' },
          React.createElement(Input, {
            value: title,
            onChange: function(e) { setTitle(e.target.value); },
            placeholder: 'Announcement title'
          }),
          React.createElement(Textarea, {
            value: message,
            onChange: function(e) { setMessage(e.target.value); },
            placeholder: 'Announcement message',
            rows: 4
          }),
          React.createElement('div', { className: 'flex gap-2' },
            React.createElement(Button, { variant: 'outline', onClick: function() { setShowCreate(false); } }, 'Cancel'),
            React.createElement(Button, {
              onClick: createAnnouncement,
              disabled: !title || !message || creating
            },
              creating ? React.createElement(Loader2, { className: 'w-4 h-4 animate-spin mr-2' }) : React.createElement(Send, { className: 'w-4 h-4 mr-2' }),
              'Send to All Users'
            )
          )
        )
      )
    ),

    // Announcements List
    announcements.length === 0
      ? React.createElement('div', { className: 'bg-white rounded-xl p-12 border border-slate-200 text-center' },
          React.createElement(Megaphone, { className: 'w-16 h-16 text-slate-300 mx-auto mb-4' }),
          React.createElement('p', { className: 'text-slate-600' }, 'No announcements yet')
        )
      : React.createElement('div', { className: 'space-y-4' },
          announcements.map(function(ann) {
            var annId = ann.id;
            var annTitle = ann.title;
            var annMessage = ann.message;
            var annCreatedAt = ann.created_at;
            
            return React.createElement('div', {
              key: annId,
              className: 'bg-white rounded-xl border border-slate-200 p-6',
              'data-testid': 'announcement-item-' + annId
            },
              React.createElement('div', { className: 'flex items-start justify-between' },
                React.createElement('div', null,
                  React.createElement('h3', { className: 'font-semibold text-slate-900' }, annTitle),
                  React.createElement('p', { className: 'text-slate-600 mt-1' }, annMessage),
                  React.createElement('p', { className: 'text-xs text-slate-400 mt-2' }, new Date(annCreatedAt).toLocaleString())
                ),
                React.createElement(Button, {
                  size: 'sm',
                  variant: 'ghost',
                  className: 'text-red-600 hover:text-red-700',
                  onClick: function() { deleteAnnouncement(annId); },
                  'data-testid': 'delete-announcement-' + annId
                },
                  React.createElement(Trash2, { className: 'w-4 h-4' })
                )
              )
            );
          })
        )
  );
}

// ==================== LOADING SPINNER ====================
function LoadingSpinner() {
  return React.createElement('div', { className: 'flex items-center justify-center py-12' },
    React.createElement('div', { className: 'w-8 h-8 border-4 border-red-500 border-t-transparent rounded-full animate-spin' })
  );
}

// ==================== PLACEHOLDER COMPONENTS ====================
// These will use the existing implementations from the old AdminDashboard

function CoursesManagement() {
  var { token } = useAuth();
  var [courses, setCourses] = useState([]);
  var [chapters, setChapters] = useState([]);
  var [loading, setLoading] = useState(true);
  var [selectedCourse, setSelectedCourse] = useState(null);
  var [showCourseModal, setShowCourseModal] = useState(false);
  var [showChapterModal, setShowChapterModal] = useState(false);
  var [editingCourse, setEditingCourse] = useState(null);
  var [editingChapter, setEditingChapter] = useState(null);
  var [draggedChapter, setDraggedChapter] = useState(null);
  var [dragOverIndex, setDragOverIndex] = useState(null);

  // Course form state
  var [courseForm, setCourseForm] = useState({
    title: '',
    description: '',
    package_type: 'core',
    thumbnail_url: '',
    is_active: true
  });

  // Chapter form state
  var [chapterForm, setChapterForm] = useState({
    title: '',
    description: '',
    video_url: '',
    duration: '',
    order: 1,
    is_free: false
  });

  useEffect(function() {
    fetchCourses();
  }, []);

  useEffect(function() {
    if (selectedCourse) {
      fetchChapters(selectedCourse.id);
    }
  }, [selectedCourse]);

  function fetchCourses() {
    setLoading(true);
    axios.get(API + '/admin/courses', {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function(res) {
      setCourses(res.data || []);
      setLoading(false);
    }).catch(function() {
      setLoading(false);
    });
  }

  function fetchChapters(courseId) {
    axios.get(API + '/admin/chapters/' + courseId, {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function(res) {
      setChapters(res.data || []);
    }).catch(function() {
      setChapters([]);
    });
  }

  function saveCourse() {
    var method = editingCourse ? 'put' : 'post';
    var url = editingCourse ? API + '/admin/courses/' + editingCourse.id : API + '/admin/courses';
    
    axios[method](url, courseForm, {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function() {
      toast.success(editingCourse ? 'Course updated' : 'Course created');
      setShowCourseModal(false);
      setEditingCourse(null);
      setCourseForm({ title: '', description: '', package_type: 'core', thumbnail_url: '', is_active: true });
      fetchCourses();
    }).catch(function() {
      toast.error('Failed to save course');
    });
  }

  function deleteCourse(courseId) {
    if (!window.confirm('Delete this course and all its chapters?')) return;
    axios.delete(API + '/admin/courses/' + courseId, {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function() {
      toast.success('Course deleted');
      if (selectedCourse && selectedCourse.id === courseId) {
        setSelectedCourse(null);
        setChapters([]);
      }
      fetchCourses();
    }).catch(function() {
      toast.error('Failed to delete course');
    });
  }

  function saveChapter() {
    var method = editingChapter ? 'put' : 'post';
    var url = editingChapter ? API + '/admin/chapters/' + editingChapter.id : API + '/admin/chapters';
    // Convert duration string to duration_minutes integer
    var durationMinutes = chapterForm.duration ? parseInt(chapterForm.duration, 10) : null;
    var data = { 
      course_id: selectedCourse.id,
      title: chapterForm.title,
      description: chapterForm.description,
      video_url: chapterForm.video_url,
      order: chapterForm.order,
      duration_minutes: durationMinutes,
      is_free: chapterForm.is_free
    };
    
    axios[method](url, data, {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function() {
      toast.success(editingChapter ? 'Chapter updated' : 'Chapter created');
      setShowChapterModal(false);
      setEditingChapter(null);
      setChapterForm({ title: '', description: '', video_url: '', duration: '', order: chapters.length + 1, is_free: false });
      fetchChapters(selectedCourse.id);
    }).catch(function(err) {
      toast.error(err.response?.data?.detail || 'Failed to save chapter');
    });
  }

  function deleteChapter(chapterId) {
    if (!window.confirm('Delete this chapter?')) return;
    axios.delete(API + '/admin/chapters/' + chapterId, {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function() {
      toast.success('Chapter deleted');
      fetchChapters(selectedCourse.id);
    }).catch(function() {
      toast.error('Failed to delete chapter');
    });
  }

  function moveChapter(chapterId, direction) {
    var idx = chapters.findIndex(function(c) { return c.id === chapterId; });
    if (idx === -1) return;
    
    var newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= chapters.length) return;
    
    // Build new order array
    var newChapters = chapters.slice();
    var temp = newChapters[idx];
    newChapters[idx] = newChapters[newIdx];
    newChapters[newIdx] = temp;
    
    // Create order updates
    var orderUpdates = newChapters.map(function(ch, i) {
      return { id: ch.id, order: i + 1 };
    });
    
    axios.post(API + '/admin/chapters/reorder', { chapters: orderUpdates }, {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function() {
      toast.success('Order updated');
      fetchChapters(selectedCourse.id);
    }).catch(function() {
      toast.error('Failed to reorder');
    });
  }

  // Drag and Drop handlers
  function handleDragStart(e, chapter, idx) {
    setDraggedChapter({ chapter: chapter, index: idx });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', chapter.id);
  }

  function handleDragOver(e, idx) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(idx);
  }

  function handleDragLeave() {
    setDragOverIndex(null);
  }

  function handleDrop(e, targetIdx) {
    e.preventDefault();
    setDragOverIndex(null);
    
    if (!draggedChapter || draggedChapter.index === targetIdx) {
      setDraggedChapter(null);
      return;
    }
    
    var newChapters = chapters.slice();
    var draggedItem = newChapters[draggedChapter.index];
    
    // Remove from old position
    newChapters.splice(draggedChapter.index, 1);
    // Insert at new position
    newChapters.splice(targetIdx, 0, draggedItem);
    
    setChapters(newChapters);
    setDraggedChapter(null);
    
    // Send reorder request to backend
    var orderUpdates = newChapters.map(function(ch, i) {
      return { id: ch.id, order: i + 1 };
    });
    
    axios.post(API + '/admin/chapters/reorder', { chapters: orderUpdates }, {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function() {
      toast.success('Order updated');
      fetchChapters(selectedCourse.id);
    }).catch(function() {
      toast.error('Failed to reorder');
      fetchChapters(selectedCourse.id);
    });
  }

  function handleDragEnd() {
    setDraggedChapter(null);
    setDragOverIndex(null);
  }

  function openEditCourse(course) {
    setEditingCourse(course);
    setCourseForm({
      title: course.title || '',
      description: course.description || '',
      package_type: course.package_type || 'core',
      thumbnail_url: course.thumbnail_url || '',
      is_active: course.is_active !== false
    });
    setShowCourseModal(true);
  }

  function openEditChapter(chapter) {
    setEditingChapter(chapter);
    setChapterForm({
      title: chapter.title || '',
      description: chapter.description || '',
      video_url: chapter.video_url || '',
      duration: chapter.duration_minutes ? String(chapter.duration_minutes) : (chapter.duration || ''),
      order: chapter.order || 1,
      is_free: chapter.is_free || false
    });
    setShowChapterModal(true);
  }

  if (loading) return React.createElement(LoadingSpinner);

  return React.createElement('div', null,
    React.createElement('div', { className: 'flex items-center justify-between mb-4' },
      React.createElement('h1', { className: 'text-2xl font-bold text-slate-900' }, 'Course Content Management'),
      React.createElement(Button, {
        onClick: function() {
          setEditingCourse(null);
          setCourseForm({ title: '', description: '', package_type: 'core', thumbnail_url: '', is_active: true });
          setShowCourseModal(true);
        }
      },
        React.createElement(Plus, { className: 'w-4 h-4 mr-2' }),
        'New Course'
      )
    ),
    
    // Info banner
    React.createElement('div', { className: 'bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6' },
      React.createElement('p', { className: 'text-blue-800 text-sm' },
        React.createElement('strong', null, 'Tip: '),
        'Manage ONE unified course for all packages. Video chapters added here will be visible to all users who have purchased any package (Video, Complete, or Premium). You can safely delete duplicate courses.'
      )
    ),

    React.createElement('div', { className: 'grid lg:grid-cols-2 gap-6' },
      // Courses List
      React.createElement('div', { className: 'bg-white rounded-xl border border-slate-200 overflow-hidden' },
        React.createElement('div', { className: 'p-4 border-b border-slate-200 bg-slate-50' },
          React.createElement('h2', { className: 'font-semibold text-slate-900' }, 'Courses')
        ),
        React.createElement('div', { className: 'divide-y divide-slate-100' },
          courses.length === 0 
            ? React.createElement('div', { className: 'p-8 text-center text-slate-500' },
                React.createElement(BookOpen, { className: 'w-12 h-12 mx-auto mb-2 text-slate-300' }),
                React.createElement('p', null, 'No courses yet')
              )
            : courses.map(function(course) {
                var courseId = course.id;
                var courseTitle = course.title;
                var courseDesc = course.description || '';
                var coursePackageType = course.package_type;
                var courseIsActive = course.is_active;
                var isSelected = selectedCourse && selectedCourse.id === courseId;
                
                return React.createElement('div', {
                  key: courseId,
                  className: 'p-4 hover:bg-slate-50 transition-colors cursor-pointer ' + (isSelected ? 'bg-red-50 border-l-4 border-red-500' : ''),
                  onClick: function() { setSelectedCourse(course); },
                  'data-testid': 'course-item-' + courseId
                },
                  React.createElement('div', { className: 'flex items-start justify-between' },
                    React.createElement('div', { className: 'flex-1' },
                      React.createElement('h3', { className: 'font-medium text-slate-900' }, courseTitle),
                      React.createElement('p', { className: 'text-sm text-slate-500 mt-1' }, courseDesc.substring(0, 100) + '...'),
                      React.createElement('div', { className: 'flex gap-2 mt-2' },
                        React.createElement(Badge, { className: 'bg-blue-100 text-blue-700 capitalize' }, coursePackageType),
                        React.createElement(Badge, { className: courseIsActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700' },
                          courseIsActive ? 'Active' : 'Inactive'
                        )
                      )
                    ),
                    React.createElement('div', { className: 'flex gap-1 ml-2' },
                      React.createElement(Button, {
                        size: 'sm',
                        variant: 'ghost',
                        onClick: function(e) { e.stopPropagation(); openEditCourse(course); },
                        'data-testid': 'edit-course-' + courseId
                      }, React.createElement(Edit, { className: 'w-4 h-4' })),
                      React.createElement(Button, {
                        size: 'sm',
                        variant: 'ghost',
                        className: 'text-red-600',
                        onClick: function(e) { e.stopPropagation(); deleteCourse(courseId); },
                        'data-testid': 'delete-course-' + courseId
                      }, React.createElement(Trash2, { className: 'w-4 h-4' }))
                    )
                  )
                );
              })
        )
      ),

      // Chapters List
      React.createElement('div', { className: 'bg-white rounded-xl border border-slate-200 overflow-hidden' },
        React.createElement('div', { className: 'p-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between' },
          React.createElement('h2', { className: 'font-semibold text-slate-900' }, 
            selectedCourse ? 'Chapters: ' + selectedCourse.title : 'Select a course'
          ),
          selectedCourse && React.createElement(Button, {
            size: 'sm',
            onClick: function() {
              setEditingChapter(null);
              setChapterForm({ title: '', description: '', video_url: '', duration: '', order: chapters.length + 1, is_free: false });
              setShowChapterModal(true);
            }
          },
            React.createElement(Plus, { className: 'w-4 h-4 mr-1' }),
            'Add Chapter'
          )
        ),
        selectedCourse 
          ? React.createElement('div', { className: 'divide-y divide-slate-100 max-h-[600px] overflow-y-auto' },
              chapters.length === 0
                ? React.createElement('div', { className: 'p-8 text-center text-slate-500' },
                    React.createElement(Play, { className: 'w-12 h-12 mx-auto mb-2 text-slate-300' }),
                    React.createElement('p', null, 'No chapters yet')
                  )
                : chapters.map(function(chapter, idx) {
                    var chapterId = chapter.id;
                    var chapterTitle = chapter.title;
                    var chapterDuration = chapter.duration_minutes ? chapter.duration_minutes + ' min' : (chapter.duration || 'No duration');
                    var chapterIsFree = chapter.is_free;
                    var isFirst = idx === 0;
                    var isLast = idx === chapters.length - 1;
                    var isDragOver = dragOverIndex === idx;
                    var isDragging = draggedChapter && draggedChapter.index === idx;
                    
                    return React.createElement('div', {
                      key: chapterId,
                      className: 'p-4 hover:bg-slate-50 border-b border-slate-100 cursor-grab active:cursor-grabbing transition-all ' + 
                        (isDragOver ? 'bg-red-50 border-red-300 border-2' : '') +
                        (isDragging ? ' opacity-50' : ''),
                      'data-testid': 'chapter-item-' + chapterId,
                      draggable: true,
                      onDragStart: function(e) { handleDragStart(e, chapter, idx); },
                      onDragOver: function(e) { handleDragOver(e, idx); },
                      onDragLeave: handleDragLeave,
                      onDrop: function(e) { handleDrop(e, idx); },
                      onDragEnd: handleDragEnd
                    },
                      React.createElement('div', { className: 'flex items-start justify-between' },
                        React.createElement('div', { className: 'flex items-start gap-3 flex-1' },
                          React.createElement('div', { className: 'flex flex-col gap-1 items-center' },
                            React.createElement('div', { 
                              className: 'w-6 h-6 flex items-center justify-center text-slate-400 cursor-grab',
                              title: 'Drag to reorder'
                            }, React.createElement(GripVertical, { className: 'w-4 h-4' })),
                            React.createElement('div', { className: 'w-6 h-6 rounded-full bg-red-100 text-red-600 flex items-center justify-center text-xs font-bold' }, idx + 1),
                            React.createElement('div', { className: 'flex flex-col gap-0.5' },
                              React.createElement(Button, {
                                size: 'sm',
                                variant: 'ghost',
                                className: 'h-5 w-5 p-0' + (isFirst ? ' opacity-30 cursor-not-allowed' : ''),
                                onClick: function(e) { e.stopPropagation(); if (!isFirst) moveChapter(chapterId, 'up'); },
                                disabled: isFirst,
                                'data-testid': 'move-up-' + chapterId
                              }, React.createElement(ChevronUp, { className: 'w-3 h-3' })),
                              React.createElement(Button, {
                                size: 'sm',
                                variant: 'ghost',
                                className: 'h-5 w-5 p-0' + (isLast ? ' opacity-30 cursor-not-allowed' : ''),
                                onClick: function(e) { e.stopPropagation(); if (!isLast) moveChapter(chapterId, 'down'); },
                                disabled: isLast,
                                'data-testid': 'move-down-' + chapterId
                              }, React.createElement(ChevronDown, { className: 'w-3 h-3' }))
                            )
                          ),
                          React.createElement('div', { className: 'flex-1' },
                            React.createElement('h4', { className: 'font-medium text-slate-900' }, chapterTitle),
                            React.createElement('p', { className: 'text-sm text-slate-500' }, chapterDuration),
                            chapterIsFree && React.createElement(Badge, { className: 'bg-green-100 text-green-700 mt-1' }, 'Free Preview')
                          )
                        ),
                        React.createElement('div', { className: 'flex gap-1' },
                          React.createElement(Button, {
                            size: 'sm',
                            variant: 'ghost',
                            onClick: function(e) { e.stopPropagation(); openEditChapter(chapter); },
                            'data-testid': 'edit-chapter-' + chapterId
                          }, React.createElement(Edit, { className: 'w-4 h-4' })),
                          React.createElement(Button, {
                            size: 'sm',
                            variant: 'ghost',
                            className: 'text-red-600',
                            onClick: function(e) { e.stopPropagation(); deleteChapter(chapterId); },
                            'data-testid': 'delete-chapter-' + chapterId
                          }, React.createElement(Trash2, { className: 'w-4 h-4' }))
                        )
                      )
                    );
                  })
            )
          : React.createElement('div', { className: 'p-8 text-center text-slate-500' },
              React.createElement(BookOpen, { className: 'w-12 h-12 mx-auto mb-2 text-slate-300' }),
              React.createElement('p', null, 'Select a course to view chapters')
            )
      )
    ),

    // Course Modal
    React.createElement(Dialog, { open: showCourseModal, onOpenChange: setShowCourseModal },
      React.createElement(DialogContent, null,
        React.createElement(DialogTitle, null, editingCourse ? 'Edit Course' : 'Create New Course'),
        React.createElement('div', { className: 'space-y-4 mt-4' },
          React.createElement('div', null,
            React.createElement('label', { className: 'block text-sm font-medium text-slate-700 mb-1' }, 'Course Title'),
            React.createElement(Input, {
              value: courseForm.title,
              onChange: function(e) { setCourseForm({ ...courseForm, title: e.target.value }); },
              placeholder: 'e.g., UK Innovator Visa Masterclass'
            })
          ),
          React.createElement('div', null,
            React.createElement('label', { className: 'block text-sm font-medium text-slate-700 mb-1' }, 'Description'),
            React.createElement(Textarea, {
              value: courseForm.description,
              onChange: function(e) { setCourseForm({ ...courseForm, description: e.target.value }); },
              placeholder: 'Course description...',
              rows: 3
            })
          ),
          React.createElement('div', null,
            React.createElement('label', { className: 'block text-sm font-medium text-slate-700 mb-1' }, 'Package Type'),
            React.createElement('select', {
              value: courseForm.package_type,
              onChange: function(e) { setCourseForm({ ...courseForm, package_type: e.target.value }); },
              className: 'w-full px-3 py-2 border border-slate-300 rounded-lg'
            },
              React.createElement('option', { value: 'core' }, 'Core'),
              React.createElement('option', { value: 'pro' }, 'Pro'),
              React.createElement('option', { value: 'premium' }, 'Premium'),
              React.createElement('option', { value: 'video' }, 'Video Course Only')
            )
          ),
          React.createElement('div', null,
            React.createElement('label', { className: 'block text-sm font-medium text-slate-700 mb-1' }, 'Thumbnail URL'),
            React.createElement(Input, {
              value: courseForm.thumbnail_url,
              onChange: function(e) { setCourseForm({ ...courseForm, thumbnail_url: e.target.value }); },
              placeholder: 'https://...'
            })
          ),
          React.createElement('div', { className: 'flex items-center gap-2' },
            React.createElement('input', {
              type: 'checkbox',
              checked: courseForm.is_active,
              onChange: function(e) { setCourseForm({ ...courseForm, is_active: e.target.checked }); },
              className: 'w-4 h-4'
            }),
            React.createElement('label', { className: 'text-sm text-slate-700' }, 'Active (visible to users)')
          ),
          React.createElement('div', { className: 'flex gap-2 pt-4' },
            React.createElement(Button, { variant: 'outline', onClick: function() { setShowCourseModal(false); } }, 'Cancel'),
            React.createElement(Button, { onClick: saveCourse, disabled: !courseForm.title }, 'Save Course')
          )
        )
      )
    ),

    // Chapter Modal
    React.createElement(Dialog, { open: showChapterModal, onOpenChange: setShowChapterModal },
      React.createElement(DialogContent, null,
        React.createElement(DialogTitle, null, editingChapter ? 'Edit Chapter' : 'Add New Chapter'),
        React.createElement('div', { className: 'space-y-4 mt-4' },
          React.createElement('div', null,
            React.createElement('label', { className: 'block text-sm font-medium text-slate-700 mb-1' }, 'Chapter Title'),
            React.createElement(Input, {
              value: chapterForm.title,
              onChange: function(e) { setChapterForm({ ...chapterForm, title: e.target.value }); },
              placeholder: 'e.g., Introduction to UK Visa System'
            })
          ),
          React.createElement('div', null,
            React.createElement('label', { className: 'block text-sm font-medium text-slate-700 mb-1' }, 'Description'),
            React.createElement(Textarea, {
              value: chapterForm.description,
              onChange: function(e) { setChapterForm({ ...chapterForm, description: e.target.value }); },
              placeholder: 'Chapter description...',
              rows: 2
            })
          ),
          React.createElement('div', null,
            React.createElement('label', { className: 'block text-sm font-medium text-slate-700 mb-1' }, 'Video URL (YouTube or direct)'),
            React.createElement(Input, {
              value: chapterForm.video_url,
              onChange: function(e) { setChapterForm({ ...chapterForm, video_url: e.target.value }); },
              placeholder: 'https://youtube.com/...'
            })
          ),
          React.createElement('div', { className: 'grid grid-cols-2 gap-4' },
            React.createElement('div', null,
              React.createElement('label', { className: 'block text-sm font-medium text-slate-700 mb-1' }, 'Duration'),
              React.createElement(Input, {
                value: chapterForm.duration,
                onChange: function(e) { setChapterForm({ ...chapterForm, duration: e.target.value }); },
                placeholder: 'e.g., 15:30'
              })
            ),
            React.createElement('div', null,
              React.createElement('label', { className: 'block text-sm font-medium text-slate-700 mb-1' }, 'Order'),
              React.createElement(Input, {
                type: 'number',
                value: chapterForm.order,
                onChange: function(e) { setChapterForm({ ...chapterForm, order: parseInt(e.target.value) || 1 }); },
                min: 1
              })
            )
          ),
          React.createElement('div', { className: 'flex items-center gap-2' },
            React.createElement('input', {
              type: 'checkbox',
              checked: chapterForm.is_free,
              onChange: function(e) { setChapterForm({ ...chapterForm, is_free: e.target.checked }); },
              className: 'w-4 h-4'
            }),
            React.createElement('label', { className: 'text-sm text-slate-700' }, 'Free Preview (available without purchase)')
          ),
          React.createElement('div', { className: 'flex gap-2 pt-4' },
            React.createElement(Button, { variant: 'outline', onClick: function() { setShowChapterModal(false); } }, 'Cancel'),
            React.createElement(Button, { onClick: saveChapter, disabled: !chapterForm.title }, 'Save Chapter')
          )
        )
      )
    )
  );
}

function TransactionsManagement() {
  var { token } = useAuth();
  var [purchases, setPurchases] = useState([]);
  var [loading, setLoading] = useState(true);

  useEffect(function() {
    axios.get(API + '/admin/purchases', {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function(res) {
      setPurchases(res.data || []);
      setLoading(false);
    }).catch(function() {
      setLoading(false);
    });
  }, []);

  if (loading) return React.createElement(LoadingSpinner);

  return React.createElement('div', null,
    React.createElement('h1', { className: 'text-2xl font-bold text-slate-900 mb-6' }, 'All Transactions'),
    React.createElement('div', { className: 'bg-white rounded-xl border border-slate-200 overflow-hidden' },
      React.createElement('table', { className: 'w-full' },
        React.createElement('thead', { className: 'bg-slate-50' },
          React.createElement('tr', null,
            React.createElement('th', { className: 'px-4 py-3 text-left text-sm font-medium text-slate-700' }, 'Date'),
            React.createElement('th', { className: 'px-4 py-3 text-left text-sm font-medium text-slate-700' }, 'User'),
            React.createElement('th', { className: 'px-4 py-3 text-left text-sm font-medium text-slate-700' }, 'Package'),
            React.createElement('th', { className: 'px-4 py-3 text-left text-sm font-medium text-slate-700' }, 'Amount'),
            React.createElement('th', { className: 'px-4 py-3 text-left text-sm font-medium text-slate-700' }, 'Status')
          )
        ),
        React.createElement('tbody', { className: 'divide-y divide-slate-100' },
          purchases.map(function(p) {
            return React.createElement('tr', { key: p.id, className: 'hover:bg-slate-50' },
              React.createElement('td', { className: 'px-4 py-3 text-sm' }, new Date(p.created_at).toLocaleDateString()),
              React.createElement('td', { className: 'px-4 py-3 text-sm' }, p.user_id?.substring(0, 8) + '...'),
              React.createElement('td', { className: 'px-4 py-3 text-sm capitalize' }, p.package_type),
              React.createElement('td', { className: 'px-4 py-3 text-sm font-mono' }, '£' + (p.amount || 0).toFixed(2)),
              React.createElement('td', { className: 'px-4 py-3' },
                React.createElement(Badge, {
                  className: p.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                }, p.status)
              )
            );
          })
        )
      )
    )
  );
}

// ==================== VIDEO COMMENTS MANAGEMENT ====================
function VideoCommentsManagement() {
  var { token } = useAuth();
  var [comments, setComments] = useState([]);
  var [loading, setLoading] = useState(true);
  var [selectedComment, setSelectedComment] = useState(null);
  var [replyText, setReplyText] = useState('');
  var [sending, setSending] = useState(false);
  var [filter, setFilter] = useState('all');

  useEffect(function() {
    fetchComments();
  }, []);

  function fetchComments() {
    setLoading(true);
    axios.get(API + '/video-comments/admin/all', {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function(res) {
      setComments(res.data.comments || []);
      setLoading(false);
    }).catch(function() {
      setLoading(false);
    });
  }

  function sendReply() {
    if (!selectedComment || !replyText.trim()) return;
    setSending(true);
    axios.put(API + '/video-comments/admin/' + selectedComment.id + '/reply', {
      reply: replyText.trim()
    }, {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function() {
      toast.success('Reply sent successfully');
      setReplyText('');
      setSelectedComment(null);
      fetchComments();
    }).catch(function() {
      toast.error('Failed to send reply');
    }).finally(function() {
      setSending(false);
    });
  }

  function deleteComment(commentId) {
    if (!window.confirm('Delete this comment?')) return;
    axios.delete(API + '/video-comments/admin/' + commentId, {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function() {
      toast.success('Comment deleted');
      fetchComments();
    }).catch(function() {
      toast.error('Failed to delete comment');
    });
  }

  function formatDate(dateStr) {
    var d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  var filteredComments = comments.filter(function(c) {
    if (filter === 'unread') return !c.is_read;
    if (filter === 'unreplied') return !c.admin_reply;
    return true;
  });

  var unreadCount = comments.filter(function(c) { return !c.is_read; }).length;

  if (loading) return React.createElement(LoadingSpinner);

  return React.createElement('div', { 'data-testid': 'video-comments-page' },
    // Header
    React.createElement('div', { className: 'flex items-center justify-between mb-6' },
      React.createElement('div', null,
        React.createElement('h1', { className: 'text-2xl font-bold text-slate-900' }, 'Video Comments'),
        React.createElement('p', { className: 'text-slate-500' }, comments.length + ' total comments, ' + unreadCount + ' unread')
      ),
      React.createElement('div', { className: 'flex gap-2' },
        React.createElement(Button, {
          variant: filter === 'all' ? 'default' : 'outline',
          size: 'sm',
          onClick: function() { setFilter('all'); }
        }, 'All'),
        React.createElement(Button, {
          variant: filter === 'unread' ? 'default' : 'outline',
          size: 'sm',
          onClick: function() { setFilter('unread'); }
        }, 'Unread'),
        React.createElement(Button, {
          variant: filter === 'unreplied' ? 'default' : 'outline',
          size: 'sm',
          onClick: function() { setFilter('unreplied'); }
        }, 'Needs Reply'),
        React.createElement(Button, { variant: 'outline', onClick: fetchComments },
          React.createElement(RefreshCw, { className: 'w-4 h-4' })
        )
      )
    ),

    // Comments List
    React.createElement('div', { className: 'grid lg:grid-cols-2 gap-4' },
      filteredComments.length === 0
        ? React.createElement('div', { className: 'col-span-2 bg-white rounded-xl p-12 text-center' },
            React.createElement(MessageCircle, { className: 'w-16 h-16 text-slate-300 mx-auto mb-4' }),
            React.createElement('p', { className: 'text-slate-500' }, 'No comments found')
          )
        : filteredComments.map(function(comment) {
            var isSelected = selectedComment && selectedComment.id === comment.id;
            var commentId = comment.id;
            
            return React.createElement('div', {
              key: commentId,
              className: 'bg-white rounded-xl border ' + (comment.is_read ? 'border-slate-200' : 'border-red-300 bg-red-50') + ' p-4 cursor-pointer hover:shadow-md transition-shadow',
              onClick: function() { setSelectedComment(isSelected ? null : comment); setReplyText(''); }
            },
              // Header
              React.createElement('div', { className: 'flex items-start justify-between mb-3' },
                React.createElement('div', { className: 'flex items-center gap-3' },
                  React.createElement('div', { className: 'w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center' },
                    React.createElement('span', { className: 'font-medium text-slate-600' }, (comment.user_name || 'U')[0].toUpperCase())
                  ),
                  React.createElement('div', null,
                    React.createElement('p', { className: 'font-medium text-slate-900' }, comment.user_name),
                    React.createElement('p', { className: 'text-xs text-slate-500' }, comment.user_email)
                  )
                ),
                React.createElement('div', { className: 'flex items-center gap-2' },
                  !comment.is_read && React.createElement(Badge, { className: 'bg-red-100 text-red-700' }, 'New'),
                  comment.admin_reply && React.createElement(Badge, { className: 'bg-green-100 text-green-700' }, 'Replied')
                )
              ),
              
              // Chapter info
              React.createElement('div', { className: 'mb-3' },
                React.createElement(Badge, { className: 'bg-blue-100 text-blue-700' }, comment.chapter_title)
              ),
              
              // Comment content
              React.createElement('p', { className: 'text-slate-700 mb-2' }, comment.content),
              React.createElement('p', { className: 'text-xs text-slate-400' }, formatDate(comment.created_at)),
              
              // AI Response preview
              comment.ai_response && React.createElement('div', { className: 'mt-3 p-2 bg-blue-50 rounded-lg' },
                React.createElement('p', { className: 'text-xs text-blue-600 font-medium mb-1' }, 'AI Response:'),
                React.createElement('p', { className: 'text-xs text-blue-800 line-clamp-2' }, comment.ai_response)
              ),
              
              // Expanded reply section
              isSelected && React.createElement('div', { className: 'mt-4 pt-4 border-t border-slate-200', onClick: function(e) { e.stopPropagation(); } },
                comment.admin_reply
                  ? React.createElement('div', { className: 'p-3 bg-green-50 rounded-lg mb-3' },
                      React.createElement('p', { className: 'text-xs text-green-600 font-medium mb-1' }, 'Your Reply:'),
                      React.createElement('p', { className: 'text-sm text-green-800' }, comment.admin_reply),
                      React.createElement('p', { className: 'text-xs text-green-500 mt-1' }, formatDate(comment.admin_replied_at))
                    )
                  : React.createElement('div', null,
                      React.createElement('textarea', {
                        value: replyText,
                        onChange: function(e) { setReplyText(e.target.value); },
                        placeholder: 'Write your reply...',
                        className: 'w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none focus:ring-2 focus:ring-red-500',
                        rows: 3
                      }),
                      React.createElement('div', { className: 'flex justify-between mt-2' },
                        React.createElement(Button, {
                          size: 'sm',
                          variant: 'destructive',
                          onClick: function() { deleteComment(commentId); }
                        },
                          React.createElement(Trash2, { className: 'w-4 h-4 mr-1' }),
                          'Delete'
                        ),
                        React.createElement(Button, {
                          size: 'sm',
                          onClick: sendReply,
                          disabled: sending || !replyText.trim(),
                          className: 'bg-red-500 hover:bg-red-600'
                        },
                          sending ? React.createElement(Loader2, { className: 'w-4 h-4 mr-1 animate-spin' }) : React.createElement(Send, { className: 'w-4 h-4 mr-1' }),
                          sending ? 'Sending...' : 'Send Reply'
                        )
                      )
                    )
              )
            );
          })
    )
  );
}

function SupportTicketsManagement() {
  var { token } = useAuth();
  var [tickets, setTickets] = useState([]);
  var [loading, setLoading] = useState(true);
  var [selectedTicket, setSelectedTicket] = useState(null);
  var [replyMessage, setReplyMessage] = useState('');
  var [sending, setSending] = useState(false);
  var [statusFilter, setStatusFilter] = useState('all');

  useEffect(function() {
    fetchTickets();
  }, [statusFilter]);

  function fetchTickets() {
    setLoading(true);
    var url = API + '/support/admin/tickets';
    if (statusFilter !== 'all') {
      url += '?status=' + statusFilter;
    }
    axios.get(url, {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function(res) {
      setTickets(res.data.tickets || []);
      setLoading(false);
    }).catch(function() {
      setLoading(false);
    });
  }

  function sendReply() {
    if (!replyMessage.trim() || !selectedTicket) return;
    setSending(true);
    axios.post(API + '/support/admin/tickets/' + selectedTicket.id + '/reply', {
      message: replyMessage
    }, {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function() {
      toast.success('Reply sent and user notified via email');
      setReplyMessage('');
      setSending(false);
      fetchTickets();
      // Refresh selected ticket
      var updated = tickets.find(function(t) { return t.id === selectedTicket.id; });
      if (updated) {
        updated.messages = updated.messages || [];
        updated.messages.push({
          id: Date.now().toString(),
          sender: 'admin',
          sender_name: 'Support Team',
          message: replyMessage,
          created_at: new Date().toISOString()
        });
        setSelectedTicket({ ...updated });
      }
    }).catch(function() {
      toast.error('Failed to send reply');
      setSending(false);
    });
  }

  function updateStatus(ticketId, newStatus) {
    axios.patch(API + '/support/admin/tickets/' + ticketId, {
      status: newStatus
    }, {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function() {
      toast.success('Status updated to ' + newStatus);
      fetchTickets();
      if (selectedTicket && selectedTicket.id === ticketId) {
        setSelectedTicket({ ...selectedTicket, status: newStatus });
      }
    }).catch(function() {
      toast.error('Failed to update status');
    });
  }

  var statusColors = {
    open: 'bg-red-100 text-red-700',
    in_progress: 'bg-yellow-100 text-yellow-700',
    resolved: 'bg-green-100 text-green-700',
    closed: 'bg-slate-100 text-slate-700'
  };

  var categoryLabels = {
    general: 'General',
    technical: 'Technical',
    billing: 'Billing',
    feature_request: 'Feature Request'
  };

  if (loading) return React.createElement(LoadingSpinner);

  return React.createElement('div', { className: 'flex gap-6 h-[calc(100vh-120px)]' },
    // Tickets List
    React.createElement('div', { className: 'w-1/3 bg-white rounded-xl border border-slate-200 flex flex-col overflow-hidden' },
      React.createElement('div', { className: 'p-4 border-b border-slate-200' },
        React.createElement('h2', { className: 'font-semibold text-slate-900 mb-3' }, 'Support Tickets'),
        React.createElement('div', { className: 'flex gap-2 flex-wrap' },
          ['all', 'open', 'in_progress', 'resolved', 'closed'].map(function(status) {
            return React.createElement('button', {
              key: status,
              onClick: function() { setStatusFilter(status); },
              className: 'px-3 py-1 text-xs rounded-full transition-colors ' + 
                (statusFilter === status ? 'bg-red-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')
            }, status === 'all' ? 'All' : status.replace('_', ' ').replace(/\b\w/g, function(l) { return l.toUpperCase(); }));
          })
        )
      ),
      React.createElement(ScrollArea, { className: 'flex-1' },
        tickets.length === 0 
          ? React.createElement('div', { className: 'p-8 text-center text-slate-500' },
              React.createElement(MessageSquare, { className: 'w-12 h-12 mx-auto mb-2 text-slate-300' }),
              React.createElement('p', null, 'No tickets found')
            )
          : React.createElement('div', { className: 'divide-y divide-slate-100' },
              tickets.map(function(ticket) {
                var isSelected = selectedTicket && selectedTicket.id === ticket.id;
                return React.createElement('button', {
                  key: ticket.id,
                  onClick: function() { setSelectedTicket(ticket); },
                  className: 'w-full p-4 text-left hover:bg-slate-50 transition-colors ' + (isSelected ? 'bg-red-50 border-l-4 border-red-500' : '')
                },
                  React.createElement('div', { className: 'flex items-start justify-between mb-1' },
                    React.createElement('p', { className: 'font-medium text-slate-900 text-sm truncate pr-2' }, ticket.subject),
                    React.createElement(Badge, { className: statusColors[ticket.status] + ' text-xs' }, ticket.status.replace('_', ' '))
                  ),
                  React.createElement('p', { className: 'text-xs text-slate-500 mb-1' }, ticket.user_name + ' • ' + (categoryLabels[ticket.category] || ticket.category)),
                  React.createElement('p', { className: 'text-xs text-slate-400' }, new Date(ticket.updated_at).toLocaleString())
                );
              })
            )
      )
    ),

    // Ticket Detail / Chat
    React.createElement('div', { className: 'flex-1 bg-white rounded-xl border border-slate-200 flex flex-col overflow-hidden' },
      selectedTicket 
        ? React.createElement(React.Fragment, null,
            // Header
            React.createElement('div', { className: 'p-4 border-b border-slate-200' },
              React.createElement('div', { className: 'flex items-start justify-between' },
                React.createElement('div', null,
                  React.createElement('h2', { className: 'font-semibold text-slate-900' }, selectedTicket.subject),
                  React.createElement('p', { className: 'text-sm text-slate-500' }, 
                    'From: ' + selectedTicket.user_name + ' (' + selectedTicket.user_email + ')'
                  ),
                  React.createElement('p', { className: 'text-xs text-slate-400 mt-1' },
                    'Category: ' + (categoryLabels[selectedTicket.category] || selectedTicket.category) + 
                    ' • Created: ' + new Date(selectedTicket.created_at).toLocaleString()
                  )
                ),
                React.createElement('div', { className: 'flex gap-2' },
                  React.createElement('select', {
                    value: selectedTicket.status,
                    onChange: function(e) { updateStatus(selectedTicket.id, e.target.value); },
                    className: 'text-sm border border-slate-200 rounded-lg px-3 py-1'
                  },
                    React.createElement('option', { value: 'open' }, 'Open'),
                    React.createElement('option', { value: 'in_progress' }, 'In Progress'),
                    React.createElement('option', { value: 'resolved' }, 'Resolved'),
                    React.createElement('option', { value: 'closed' }, 'Closed')
                  )
                )
              )
            ),
            // Messages
            React.createElement(ScrollArea, { className: 'flex-1 p-4' },
              React.createElement('div', { className: 'space-y-4' },
                (selectedTicket.messages || []).map(function(msg) {
                  var isAdmin = msg.sender === 'admin';
                  return React.createElement('div', {
                    key: msg.id,
                    className: 'flex ' + (isAdmin ? 'justify-end' : 'justify-start')
                  },
                    React.createElement('div', {
                      className: 'max-w-[70%] rounded-2xl px-4 py-3 ' + 
                        (isAdmin ? 'bg-red-500 text-white' : 'bg-slate-100 text-slate-900')
                    },
                      React.createElement('p', { className: 'text-xs mb-1 ' + (isAdmin ? 'text-red-100' : 'text-slate-500') }, 
                        msg.sender_name + ' • ' + new Date(msg.created_at).toLocaleString()
                      ),
                      React.createElement('p', { className: 'text-sm whitespace-pre-wrap' }, msg.message)
                    )
                  );
                })
              )
            ),
            // Reply Input
            React.createElement('div', { className: 'p-4 border-t border-slate-200' },
              React.createElement('div', { className: 'flex gap-3' },
                React.createElement(Textarea, {
                  value: replyMessage,
                  onChange: function(e) { setReplyMessage(e.target.value); },
                  placeholder: 'Type your reply... (Email will be sent to user)',
                  rows: 2,
                  className: 'flex-1'
                }),
                React.createElement(Button, {
                  onClick: sendReply,
                  disabled: !replyMessage.trim() || sending,
                  className: 'self-end'
                },
                  sending 
                    ? React.createElement(Loader2, { className: 'w-4 h-4 animate-spin' })
                    : React.createElement(Send, { className: 'w-4 h-4' })
                )
              )
            )
          )
        : React.createElement('div', { className: 'flex-1 flex items-center justify-center text-slate-500' },
            React.createElement('div', { className: 'text-center' },
              React.createElement(MessageSquare, { className: 'w-16 h-16 mx-auto mb-4 text-slate-300' }),
              React.createElement('p', null, 'Select a ticket to view conversation')
            )
          )
    )
  );
}

function ZoomBookingsManagement() {
  var { token } = useAuth();
  var [bookings, setBookings] = useState([]);
  var [loading, setLoading] = useState(true);
  var [statusFilter, setStatusFilter] = useState('all');
  var [editingBooking, setEditingBooking] = useState(null);
  var [zoomLink, setZoomLink] = useState('');
  var [adminNotes, setAdminNotes] = useState('');

  useEffect(function() {
    fetchBookings();
  }, [statusFilter]);

  function fetchBookings() {
    setLoading(true);
    var url = API + '/bookings/admin/all';
    if (statusFilter !== 'all') {
      url += '?status=' + statusFilter;
    }
    axios.get(url, {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function(res) {
      setBookings(res.data.bookings || []);
      setLoading(false);
    }).catch(function() {
      setLoading(false);
    });
  }

  function updateBooking(bookingId, updates) {
    axios.patch(API + '/bookings/admin/' + bookingId, updates, {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function() {
      toast.success('Booking updated');
      fetchBookings();
      setEditingBooking(null);
    }).catch(function() {
      toast.error('Failed to update');
    });
  }

  function openEditModal(booking) {
    setEditingBooking(booking);
    setZoomLink(booking.zoom_link || '');
    setAdminNotes(booking.admin_notes || '');
  }

  var statusColors = {
    pending: 'bg-yellow-100 text-yellow-700',
    confirmed: 'bg-blue-100 text-blue-700',
    completed: 'bg-green-100 text-green-700',
    cancelled: 'bg-red-100 text-red-700'
  };

  if (loading) return React.createElement(LoadingSpinner);

  return React.createElement('div', null,
    React.createElement('div', { className: 'flex items-center justify-between mb-6' },
      React.createElement('h1', { className: 'text-2xl font-bold text-slate-900' }, 'Zoom Bookings'),
      React.createElement('div', { className: 'flex gap-2' },
        ['all', 'pending', 'confirmed', 'completed', 'cancelled'].map(function(status) {
          return React.createElement('button', {
            key: status,
            onClick: function() { setStatusFilter(status); },
            className: 'px-3 py-1 text-sm rounded-full transition-colors ' + 
              (statusFilter === status ? 'bg-red-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')
          }, status.charAt(0).toUpperCase() + status.slice(1));
        })
      )
    ),

    bookings.length === 0 
      ? React.createElement('div', { className: 'bg-white rounded-xl p-12 border border-slate-200 text-center' },
          React.createElement(Calendar, { className: 'w-16 h-16 text-slate-300 mx-auto mb-4' }),
          React.createElement('p', { className: 'text-slate-600' }, 'No bookings found')
        )
      : React.createElement('div', { className: 'bg-white rounded-xl border border-slate-200 overflow-hidden' },
          React.createElement('table', { className: 'w-full' },
            React.createElement('thead', { className: 'bg-slate-50' },
              React.createElement('tr', null,
                React.createElement('th', { className: 'px-4 py-3 text-left text-sm font-medium text-slate-700' }, 'Date & Time'),
                React.createElement('th', { className: 'px-4 py-3 text-left text-sm font-medium text-slate-700' }, 'User'),
                React.createElement('th', { className: 'px-4 py-3 text-left text-sm font-medium text-slate-700' }, 'Topic'),
                React.createElement('th', { className: 'px-4 py-3 text-left text-sm font-medium text-slate-700' }, 'Status'),
                React.createElement('th', { className: 'px-4 py-3 text-left text-sm font-medium text-slate-700' }, 'Zoom Link'),
                React.createElement('th', { className: 'px-4 py-3 text-left text-sm font-medium text-slate-700' }, 'Actions')
              )
            ),
            React.createElement('tbody', { className: 'divide-y divide-slate-100' },
              bookings.map(function(booking) {
                var bookingId = booking.id;
                var bookingDate = booking.date;
                var bookingTimeSlot = booking.time_slot;
                var bookingUserName = booking.user_name;
                var bookingUserEmail = booking.user_email;
                var bookingTopic = booking.topic;
                var bookingStatus = booking.status;
                var bookingZoomLink = booking.zoom_link;
                
                return React.createElement('tr', { key: bookingId, className: 'hover:bg-slate-50', 'data-testid': 'booking-row-' + bookingId },
                  React.createElement('td', { className: 'px-4 py-3' },
                    React.createElement('p', { className: 'font-medium text-slate-900' }, bookingDate),
                    React.createElement('p', { className: 'text-sm text-slate-500' }, bookingTimeSlot)
                  ),
                  React.createElement('td', { className: 'px-4 py-3' },
                    React.createElement('p', { className: 'font-medium text-slate-900' }, bookingUserName),
                    React.createElement('p', { className: 'text-sm text-slate-500' }, bookingUserEmail)
                  ),
                  React.createElement('td', { className: 'px-4 py-3 max-w-xs' },
                    React.createElement('p', { className: 'text-sm text-slate-900 truncate' }, bookingTopic)
                  ),
                  React.createElement('td', { className: 'px-4 py-3' },
                    React.createElement(Badge, { className: statusColors[bookingStatus] }, bookingStatus)
                  ),
                  React.createElement('td', { className: 'px-4 py-3' },
                    bookingZoomLink 
                      ? React.createElement('a', {
                          href: bookingZoomLink,
                          target: '_blank',
                          rel: 'noopener noreferrer',
                          className: 'text-blue-600 hover:underline text-sm'
                        }, 'Join Link')
                      : React.createElement('span', { className: 'text-slate-400 text-sm' }, 'Not set')
                  ),
                  React.createElement('td', { className: 'px-4 py-3' },
                    React.createElement('div', { className: 'flex gap-2' },
                      React.createElement(Button, {
                        size: 'sm',
                        variant: 'outline',
                        onClick: function() { openEditModal(booking); },
                        'data-testid': 'edit-booking-' + bookingId
                      },
                        React.createElement(Edit, { className: 'w-4 h-4' })
                      ),
                      bookingStatus === 'pending' && React.createElement(Button, {
                        size: 'sm',
                        className: 'bg-green-600 hover:bg-green-700',
                        onClick: function() { updateBooking(bookingId, { status: 'confirmed' }); },
                        'data-testid': 'confirm-booking-' + bookingId
                      }, 'Confirm'),
                      bookingStatus === 'confirmed' && React.createElement(Button, {
                        size: 'sm',
                        onClick: function() { updateBooking(bookingId, { status: 'completed' }); },
                        'data-testid': 'complete-booking-' + bookingId
                      }, 'Complete')
                    )
                  )
                );
              })
            )
          )
        ),

    // Edit Modal
    React.createElement(Dialog, { open: !!editingBooking, onOpenChange: function() { setEditingBooking(null); } },
      React.createElement(DialogContent, null,
        React.createElement(DialogTitle, null, 'Edit Booking'),
        editingBooking && React.createElement('div', { className: 'space-y-4 mt-4' },
          React.createElement('div', { className: 'bg-slate-50 rounded-lg p-4' },
            React.createElement('p', { className: 'text-sm' }, React.createElement('span', { className: 'text-slate-500' }, 'User: '), editingBooking.user_name),
            React.createElement('p', { className: 'text-sm' }, React.createElement('span', { className: 'text-slate-500' }, 'Date: '), editingBooking.date + ' at ' + editingBooking.time_slot),
            React.createElement('p', { className: 'text-sm' }, React.createElement('span', { className: 'text-slate-500' }, 'Topic: '), editingBooking.topic)
          ),
          React.createElement('div', null,
            React.createElement('label', { className: 'block text-sm font-medium text-slate-700 mb-1' }, 'Status'),
            React.createElement('select', {
              value: editingBooking.status,
              onChange: function(e) { setEditingBooking({ ...editingBooking, status: e.target.value }); },
              className: 'w-full px-3 py-2 border border-slate-300 rounded-lg'
            },
              React.createElement('option', { value: 'pending' }, 'Pending'),
              React.createElement('option', { value: 'confirmed' }, 'Confirmed'),
              React.createElement('option', { value: 'completed' }, 'Completed'),
              React.createElement('option', { value: 'cancelled' }, 'Cancelled')
            )
          ),
          React.createElement('div', null,
            React.createElement('label', { className: 'block text-sm font-medium text-slate-700 mb-1' }, 'Zoom Link'),
            React.createElement(Input, {
              value: zoomLink,
              onChange: function(e) { setZoomLink(e.target.value); },
              placeholder: 'https://zoom.us/j/...'
            })
          ),
          React.createElement('div', null,
            React.createElement('label', { className: 'block text-sm font-medium text-slate-700 mb-1' }, 'Admin Notes'),
            React.createElement(Textarea, {
              value: adminNotes,
              onChange: function(e) { setAdminNotes(e.target.value); },
              placeholder: 'Internal notes...',
              rows: 2
            })
          ),
          React.createElement('div', { className: 'flex gap-2 pt-4' },
            React.createElement(Button, { variant: 'outline', onClick: function() { setEditingBooking(null); } }, 'Cancel'),
            React.createElement(Button, {
              onClick: function() {
                updateBooking(editingBooking.id, {
                  status: editingBooking.status,
                  zoom_link: zoomLink,
                  admin_notes: adminNotes
                });
              }
            }, 'Save Changes')
          )
        )
      )
    )
  );
}

function TemplatesManagement() {
  return React.createElement('div', null,
    React.createElement('h1', { className: 'text-2xl font-bold text-slate-900 mb-6' }, 'Templates'),
    React.createElement('p', { className: 'text-slate-600' }, 'Templates management - existing implementation')
  );
}

// ==================== MAIN COMPONENT ====================
function AdminDashboardNew() {
  var [sidebarOpen, setSidebarOpen] = useState(false);
  var [notificationCount, setNotificationCount] = useState(0);
  var { token } = useAuth();

  useEffect(function() {
    // Fetch notification count
    axios.get(API + '/admin/notifications?unread_only=true', {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function(res) {
      setNotificationCount((res.data.notifications || []).length);
    }).catch(function() {});
  }, []);

  return React.createElement('div', { className: 'min-h-screen bg-slate-50 flex', 'data-testid': 'admin-dashboard' },
    React.createElement(AdminSidebar, {
      isOpen: sidebarOpen,
      onClose: function() { setSidebarOpen(false); },
      notificationCount: notificationCount
    }),
    
    sidebarOpen && React.createElement('div', {
      className: 'fixed inset-0 bg-black/50 z-30 lg:hidden',
      onClick: function() { setSidebarOpen(false); }
    }),

    React.createElement('div', { className: 'flex-1 min-w-0' },
      // Header
      React.createElement('header', { className: 'bg-white border-b border-slate-200 p-4 sticky top-0 z-20' },
        React.createElement('div', { className: 'flex items-center justify-between' },
          React.createElement('div', { className: 'flex items-center gap-4' },
            React.createElement('button', {
              onClick: function() { setSidebarOpen(!sidebarOpen); },
              className: 'lg:hidden text-slate-600 hover:text-slate-900'
            },
              React.createElement(Menu, { className: 'w-6 h-6' })
            ),
            React.createElement('span', { className: 'text-lg font-semibold text-slate-900' }, 'Admin Dashboard')
          ),
          React.createElement('div', { className: 'flex items-center gap-4' },
            React.createElement('button', { className: 'relative p-2 text-slate-600 hover:text-slate-900' },
              React.createElement(Bell, { className: 'w-6 h-6' }),
              notificationCount > 0 && React.createElement('span', {
                className: 'absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center'
              }, notificationCount)
            )
          )
        )
      ),

      // Main Content
      React.createElement('main', { className: 'p-6' },
        React.createElement(Routes, null,
          React.createElement(Route, { index: true, element: React.createElement(DashboardOverview) }),
          React.createElement(Route, { path: 'users', element: React.createElement(UserManagement) }),
          React.createElement(Route, { path: 'packages', element: React.createElement(PackagePricing) }),
          React.createElement(Route, { path: 'courses', element: React.createElement(CoursesManagement) }),
          React.createElement(Route, { path: 'comments', element: React.createElement(VideoCommentsManagement) }),
          React.createElement(Route, { path: 'purchases', element: React.createElement(TransactionsManagement) }),
          React.createElement(Route, { path: 'pending-payments', element: React.createElement(PendingPayments) }),
          React.createElement(Route, { path: 'tickets', element: React.createElement(SupportTicketsManagement) }),
          React.createElement(Route, { path: 'bookings', element: React.createElement(ZoomBookingsManagement) }),
          React.createElement(Route, { path: 'announcements', element: React.createElement(AnnouncementsManagement) }),
          React.createElement(Route, { path: 'templates', element: React.createElement(TemplatesManagement) })
        )
      )
    )
  );
}

export default AdminDashboardNew;
